import { Link } from 'react-router-dom';
import { Logo } from '../components/Logo';

const UPDATED = 'June 4, 2026';
const ENTITY = 'AJM Aviation Ventures, LLC';

function LegalShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-sky-50/40">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <Link to="/">
            <Logo className="h-7" />
          </Link>
          <div className="flex gap-4 text-sm text-ink-muted">
            <Link to="/privacy" className="hover:text-navy-900">Privacy</Link>
            <Link to="/terms" className="hover:text-navy-900">Terms</Link>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="font-display text-3xl font-bold text-navy-900">{title}</h1>
        <p className="mt-1 text-sm text-ink-muted">Last updated {UPDATED}</p>
        <div className="legal mt-6 space-y-5 text-sm leading-relaxed text-ink">{children}</div>
        <p className="mt-10 border-t border-slate-200 pt-5 text-xs text-ink-muted">
          {ENTITY} · 101 Main Street, Ste H, Seal Beach, CA 90740
        </p>
      </main>
    </div>
  );
}

function H({ children }: { children: React.ReactNode }) {
  return <h2 className="font-display text-lg font-semibold text-navy-900 pt-2">{children}</h2>;
}

export function PrivacyPolicy() {
  return (
    <LegalShell title="Privacy Policy">
      <p>
        This Privacy Policy explains how {ENTITY} (“AJM,” “we,” “us”) collects, uses, and protects
        information in the AJM Aviation lessee portal (the “Portal”). It covers the Portal software only;
        your use of the aircraft is governed by your signed Aircraft Dry Lease Agreement.
      </p>

      <H>Information we collect</H>
      <ul className="list-disc space-y-1 pl-5">
        <li><strong>Account:</strong> name, email, phone, mailing address, and emergency contact.</li>
        <li><strong>Pilot details:</strong> certificate type and number, ratings, flight time, and medical certificate class and expiration.</li>
        <li><strong>Documents:</strong> images you upload (e.g., pilot certificate, medical, government ID, airport badge) for verification.</li>
        <li><strong>Flight records:</strong> dates, Hobbs/tach readings, hours, destinations, fuel, and squawks you report.</li>
        <li><strong>Payment:</strong> a payment method stored with our processor, Stripe. We do not store full card numbers on our systems.</li>
        <li><strong>Usage:</strong> basic technical data (such as IP address and timestamps) needed to operate and secure the Portal.</li>
      </ul>

      <H>How we use it</H>
      <ul className="list-disc space-y-1 pl-5">
        <li>Operate the lease relationship: verify eligibility, schedule, log flights, and track aircraft time and airworthiness.</li>
        <li>Bill and process payments in accordance with your lease.</li>
        <li>Send you account, billing, safety, and currency communications.</li>
        <li>Maintain records required for safety, insurance, and legal compliance.</li>
      </ul>

      <H>How we share it</H>
      <p>We do not sell your information. We share it only with service providers that help us run the Portal:</p>
      <ul className="list-disc space-y-1 pl-5">
        <li><strong>Stripe</strong> — payment processing.</li>
        <li><strong>Google Firebase</strong> — authentication, database, and document storage.</li>
        <li><strong>Resend</strong> — transactional email delivery.</li>
      </ul>
      <p>We may also disclose information when required by law or to protect safety, our rights, or the aircraft.</p>

      <H>Security &amp; retention</H>
      <p>
        Data is encrypted in transit and protected by access controls. We keep your information for as long
        as your account is active and as needed to meet legal, tax, insurance, and safety-record obligations.
      </p>

      <H>Your choices</H>
      <p>
        You may request access to or correction of your information, and you may close your account. Some
        records (e.g., flight and billing history) must be retained for compliance even after closure.
        Contact us at the number or address below.
      </p>
    </LegalShell>
  );
}

export function Terms() {
  return (
    <LegalShell title="Terms of Use">
      <p>
        These Terms govern your use of the AJM Aviation lessee portal (the “Portal”) provided by {ENTITY}.
        They apply to the software and services only.
      </p>

      <div className="rounded-lg border border-navy-900/10 bg-sky-50 px-4 py-3">
        <strong className="text-navy-900">The lease controls.</strong> Your right to operate the aircraft,
        the rates and minimums you owe, and all flight-operations obligations are set by your signed{' '}
        <em>Aircraft Dry Lease Agreement</em>. If anything in the Portal ever conflicts with that
        agreement, the signed lease governs.
      </div>

      <H>Your account</H>
      <ul className="list-disc space-y-1 pl-5">
        <li>Provide accurate, current information and keep your pilot and payment details up to date.</li>
        <li>Keep your login credentials confidential; you are responsible for activity under your account.</li>
        <li>Use the Portal only for managing your own authorized use of the aircraft.</li>
      </ul>

      <H>Flight logging &amp; billing</H>
      <p>
        You agree to log each flight accurately, including Hobbs and tach readings. Charges are calculated
        and processed automatically based on your billing profile and the terms of your lease, using the
        payment method you keep on file. You authorize those charges. Tampering with readings or billing
        classifications is a breach of your lease.
      </p>

      <H>Acceptable use</H>
      <p>
        Do not attempt to disrupt, reverse-engineer, or gain unauthorized access to the Portal, and do not
        upload unlawful content. We may suspend access for non-payment, safety concerns, or violation of
        these Terms or the lease.
      </p>

      <H>Disclaimers &amp; limitation of liability</H>
      <p>
        The Portal is provided “as is” without warranties of any kind. AJM is not liable for indirect or
        consequential damages arising from the software. Nothing here limits any rights or obligations under
        the signed lease, applicable aviation regulations, or law. These Terms are governed by the laws of
        the State of Arizona.
      </p>

      <H>Changes</H>
      <p>We may update these Terms; continued use after an update means you accept the revised Terms.</p>
    </LegalShell>
  );
}
